import React, { useState } from 'react';
import { compose } from 'redux';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import {
  usePaginatedQuery, ErrorHandler, InfiniteScrollComponent, captureException,
} from '@playbookux/playbook-web-core';

import Table from './custom-table';
import AddTesterModal from './add-tester-modal';

import withLayout from '../../../core/components/with-layout';
import {
  GET_USER_ELIGIBILITIES_BY_COMPANY,
  LIMIT,
  ORDER,
  UPDATE_USER_ELIGIBILITY,
  CREATE_USER_ELIGIBILITY,
  handleCreateUserEligibityMutationUpdate,
} from './graphql';

import {
  Button,
  Container,
  HeaderContainer,
  Text,
} from './styles';

const PastTesters = (props) => {
  const [showAddTesterModal, setShowAddTesterModal] = useState(false);
  const [addEligibilityError, setAddEligibilityError] = useState(null);
  const { match: { params } } = props;
  const { companyId } = params;

  const attributes = {
    variables: {
      companyId,
      limit: LIMIT,
      offset: 0,
      order: ORDER,
    },
  };

  const {
    data = {},
    fetchMore,
    hasMore,
    error,
  } = usePaginatedQuery(GET_USER_ELIGIBILITIES_BY_COMPANY, attributes, { dataField: 'userEligibilities' });

  const { userEligibilities = [] } = data;

  const [updateUserEligibility] = useMutation(UPDATE_USER_ELIGIBILITY);
  const [createUserEligibility, { loading: creatingUserEligibility }] = useMutation(CREATE_USER_ELIGIBILITY, {
    update: handleCreateUserEligibityMutationUpdate,
  });

  const handleUpdateEligibility = async (userId, eligibility) => {
    try {
      await updateUserEligibility({
        variables: {
          userId,
          companyId,
          eligibility,
        },
      });
    } catch (err) {
      captureException('UPDATE_USER_ELIGIBILITY_FAILURE', err);
    }
  };

  const handleAddEligibility = async (identifier, date) => {
    try {
      await createUserEligibility({
        variables: {
          identifier,
          companyId,
          dateLastTest: date,
        },
      });

      setAddEligibilityError(null);
      setShowAddTesterModal(false);
    } catch (err) {
      captureException('CREATE_USER_ELIGIBILITY_FAILURE', err);
      setAddEligibilityError(err);
    }
  };

  if (error) {
    return (
      <Container>
        <ErrorHandler
          error={error}
          defaultMessage="Error while retrieving past testers."
        />
      </Container>
    );
  }

  return (
    <Container>
      <HeaderContainer>
        <Text.Bold>Setting: New testers in 365 days</Text.Bold>
        <Button onClick={() => setShowAddTesterModal(true)}>
          Add Tester
        </Button>
      </HeaderContainer>

      <AddTesterModal
        error={addEligibilityError}
        loading={creatingUserEligibility}
        show={showAddTesterModal}
        onClose={() => setShowAddTesterModal(false)}
        onSubmit={handleAddEligibility}
      />

      <InfiniteScrollComponent
        useWindow
        hasMore={hasMore}
        onLoadMore={fetchMore}
      >
        <Table
          onUpdateEligibility={handleUpdateEligibility}
          testers={userEligibilities}
        />
      </InfiniteScrollComponent>
    </Container>
  );
};

PastTesters.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      companyId: PropTypes.string,
    }),
  }).isRequired,
};

export default compose(
  withLayout(),
)(PastTesters);
